<template>
  <div>
    <template v-if="items">
      <div v-for="item in items" v-if="(item.key != 'TAX') || parseInt(item.unit)" class="mb-1">
        <div class="d-flex align-start text-body-2">
          <div v-if="item.key == 'TAX'">{{item.meta.description}}</div>
          <div v-else>{{item.title || item.key}}</div>
          <v-btn
            v-if="item.disclaimer"
            class="ml-2"
            icon x-small color="grey"
            @click="$confirm({
              title: item.title,
              msg: item.disclaimer,
              buttons: [ 'Ok' ],
              primary: 'Ok',
              persistent: false
            })"
          >
            <v-icon v-html="mdiInformation" />
          </v-btn>
          <div v-if="item.qty > 1" class="ml-2 grey--text" style="white-space: nowrap;">× {{item.qty}}</div>
          <v-spacer />
          <price
            class="ml-2 flex-grow-0"
            :color="item.unit.startsWith('-') ? 'green' : 'black'"
            :value="(parseInt(item.unit.replace('.', '')) * item.qty / 100).toFixed(2)"
          />
        </div>
        <div
          v-if="item.subtitle"
          class="ml-4 overline short grey--text pr-16"
          style="font-size: .6rem !important; line-height: .8rem !important;"
        >
          <template v-if="item.subtitle == 'DESIGN'"></template>
          <template v-else-if="item.subtitle == 'VIDEO'"></template>
          <!-- Note that you used to actually show a preview / link of the actual video and card used, but that just confuses people and takes up space -->
          <!--
          <router-link
            v-if="item.subtitle == 'DESIGN'"
            class="d-inline-block mt-1"
            style="width: unset;"
            target="_blank"
            :to="{ name: 'catalog', params: { designId: item.meta.design_id } }"
          >
            <div class="d-flex align-center">
              <v-responsive class="overflow-visible flex-grow-0" width="30" :aspect-ratio="5 / 7">
                <v-card v-if="designs[item.meta.design_id]" tile width="30">
                  <card-preview mode="preview" :width="30" :card="designs[item.meta.design_id]" />
                </v-card>
              </v-responsive>
              <v-icon class="ml-2" small color="primary" v-html="mdiOpenInNew" />
            </div>
          </router-link>
          <router-link
            v-else-if="item.subtitle == 'VIDEO'"
            class="d-inline-block mt-1"
            target="_blank"
            :to="{ name: 'catalog-video', params: { templateId: item.meta.template_id } }"
          >
            <div class="d-flex align-center">
              <v-card tile width="75">
                <v-img :aspect-ratio="16 / 9" :src="videos[item.meta.template_id]?.poster" :title="item.meta.name" />
              </v-card>
              <v-icon class="ml-2" small color="primary" v-html="mdiOpenInNew" />
            </div>
          </router-link>
          -->
          <template v-else-if="item.subtitle == 'GIFTCARD'">
            <currency :code="item.meta.cur" />{{item.meta.amt + ' – ' + item.meta.name}}
          </template>
          <template v-else-if="item.subtitle == 'CODE'">{{item.meta.code}}</template>
          <template v-else-if="item.subtitle == 'PCT'">
            {{(item.meta.pct * 100) + '% × ' + item.meta.subtotal}}
          </template>
          <template v-else-if="item.subtitle == 'SUB'">
            {{item.meta.code}} • {{item.meta.cadence}}<span v-if="item.meta.code == 'MULTI-USER'"> • up to {{item.meta.max}} users</span>
          </template>
          <template v-else-if="item.subtitle == 'SUB_CREDIT'">
            {{item.meta.days_total - item.meta.days_elapsed}} / {{item.meta.days_total}} days unused
          </template>
          <template v-else>{{item.meta}}</template>
        </div>
      </div>
      <v-divider class="my-3" />
    </template>
    <div class="d-flex font-weight-bold">
      <div>Total</div>
      <v-spacer />
      <price :value="itemized.price" />
    </div>
  </div>
</template>


<script>
import CardPreview from '@/components/CardPreview.vue';
import Currency from '@/components/Currency.vue';
import Price from '@/components/Price.vue';
import { publicDesign } from '@/mixins/public-design.js';
import { publicGroupVideoTemplate } from '@/mixins/public-group-video-template.js';
import { mdiInformation, mdiOpenInNew } from '@mdi/js';


// Note: if any of the types you want to display will display differently in its itemized row depending on its
// metadata, you need to implement the 'rowKey' function, which takes the item's metadata, and returns an identifier
// unique to its row. This allows us to combine rows that appear the same to the user, but might have different
// metadata.

const DETAILS = {
  CARD_PERSONAL: {
    title: 'Personal card',
    subtitle: 'DESIGN'
  },
  CARD_GROUP: {
    title: 'Group card',
    subtitle: 'DESIGN'
  },
  CARD_INVITATION: {
    title: 'Event invitation (with responses)',
    subtitle: 'DESIGN'
  },
  GROUP_VIDEO: {
    title: 'Group video',
    subtitle: 'VIDEO'
  },
  GIFTCARD: {
    title: 'Gift card',
    subtitle: 'GIFTCARD',
    rowKey: meta => meta.amt + ' ' + meta.name
  },
  GIFTCARD_FEE: {
    title: 'Gift card service charge',
    disclaimer: '$0.50 + 5% of gift card value'
  },
  ADDTL_RECIPIENTS_BLOCK: {
    title: 'Block of 25 additional recipients',
    disclaimer: 'First 25 recipients are free, $1 per every 25 additional recipients'
  },
  ADDTL_CONTRIBUTORS_BLOCK: {
    title: 'Block of 25 additional contributors',
    disclaimer: 'First 25 invited contributors are free, $1 per every 25 additional contributors'
  },
  THEME_BG: {
    title: 'Custom confetti or background'
  },
  THEME_ENV: {
    title: 'Custom stamp, envelope color, or font'
  },
  THEME_LOGO: {
    title: 'Custom logo'
  },
  THEME_MUSIC: {
    title: 'Custom opening music'
  },
  PRIVATE_PAGES: {
    title: 'Private signing'
  },
  OPEN_MUSIC: {
    title: 'Opening music'
  },
  BULK_PACK: {
    title: 'Bulk pack',
    subtitle: 'CODE',
    rowKey: meta => meta.code
  },
  CARD_CREDIT: {
    title: 'Card credit',
    subtitle: 'CODE',
    rowKey: meta => meta.code
  },
  VIDEO_CREDIT: {
    title: 'Video credit',
    subtitle: 'CODE',
    rowKey: meta => meta.code
  },
  CREDIT: {
    title: 'Credit',
    disclaimer: 'Monetary credit does not apply to gift cards'
  },
  CREDIT_GIFTCARD: {
    title: 'Credit (gift cards)'
  },
  PCT_DISCOUNT: {
    title: 'Percent discount',
    subtitle: 'PCT',
    disclaimer: 'Percent discounts do not apply to gift cards',
    rowKey: meta => meta.pct + ' ' + meta.subtotal
  },
  SUBSCRIPTION: {
    title: 'Unlimited subscription',
    subtitle: 'SUB',
    disclaimer: 'This is a recurring payment – you will be billed automatically until you change / cancel your subscription'
  },
  SUBSCRIPTION_CREDIT: {
    title: 'Existing subscription credit',
    subtitle: 'SUB_CREDIT'
  },
  SUBSCRIPTION_DISCOUNT: {
    title: 'Unlimited subscription discount'
  }
};


export default {
  title: 'itemized-price',

  mixins: [ publicDesign, publicGroupVideoTemplate ],

  components: { CardPreview, Currency, Price },

  props: [ 'itemized' ],

  data() {
    return {
      designs: {},
      videos: {}
    };
  },

  computed: {
    items() {
      if (!this.itemized.items)
        return null;
      const items = this.itemized.items.map(x => ({ ...x, ...DETAILS[x.key] }));
      const itemsByRowKey = {};
      for (const item of items) {
        const rowKey = item.key + ' ' + item.unit + (item.rowKey ? ' ' + item.rowKey(item.meta) : '');
        if (itemsByRowKey[rowKey])
          itemsByRowKey[rowKey].qty += item.qty;
        else
          itemsByRowKey[rowKey] = item;
      }
      return Object.values(itemsByRowKey);
    }
  },

  created() {
    Object.assign(this, { mdiInformation, mdiOpenInNew });
  },

  watch: {
    items: {
      immediate: true,
      async handler(v) {
        for (const item of v || []) {
          if (item.subtitle == 'DESIGN')
            this.designs = { ...this.designs, [item.meta.design_id]: await this.getPublicDesign(item.meta.design_id) };
          else if (item.subtitle == 'VIDEO')
            this.videos = { ...this.videos, [item.meta.template_id]: await this.getPublicGroupVideoTemplate(item.meta.template_id) };
        }
      }
    }
  }
}
</script>
